import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { KeyRound, AlertCircle, CheckCircle2 } from 'lucide-react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (newPassword.length < 10) { setError('Wachtwoord moet minstens 10 tekens zijn'); return; }
    if (newPassword !== confirmPassword) { setError('Wachtwoorden komen niet overeen'); return; }
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: newPassword });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Resetten mislukt — link is mogelijk verlopen');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex h-screen items-center justify-center bg-gradient-to-br from-tw1-brand-navy-top to-tw1-brand-navy font-sans px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center text-white">
          <img src="/images/logo.png" alt="Truckwash1" className="h-14 w-auto object-contain mb-4" />
          <h1 className="text-3xl font-bold tracking-tight">SmartRoster</h1>
        </div>

        <div className="rounded-xl bg-white p-8 shadow-2xl">
          <h2 className="text-xl font-semibold text-slate-900 mb-6 text-center">Nieuw wachtwoord instellen</h2>

          {!token ? (
            <div role="alert" data-testid="reset-password-no-token" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>Geen (geldig) reset-token gevonden. Vraag opnieuw een link aan.</span>
            </div>
          ) : done ? (
            <div data-testid="reset-password-success" className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-800">
              <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
              <span>Wachtwoord ingesteld! Je wordt doorgestuurd naar het inlogscherm.</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <label htmlFor="np" className="block text-sm font-medium text-slate-700 mb-1.5">Nieuw wachtwoord</label>
                <input
                  id="np" type="password" required minLength={10} value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password"
                  data-testid="reset-password-new"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-tw1-accent-gold focus:outline-none focus:ring-2 focus:ring-tw1-accent-gold/30"
                />
              </div>
              <div>
                <label htmlFor="cp" className="block text-sm font-medium text-slate-700 mb-1.5">Bevestig wachtwoord</label>
                <input
                  id="cp" type="password" required minLength={10} value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password"
                  data-testid="reset-password-confirm"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-tw1-accent-gold focus:outline-none focus:ring-2 focus:ring-tw1-accent-gold/30"
                />
              </div>
              {error && (
                <div role="alert" data-testid="reset-password-error" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <button
                type="submit" disabled={submitting} data-testid="reset-password-submit"
                className="flex w-full items-center justify-center gap-2 rounded-md bg-tw1-accent-gold px-4 py-2.5 text-sm font-bold text-tw1-brand-navy shadow hover:bg-tw1-accent-gold-dark disabled:opacity-60 transition-colors"
              >
                <KeyRound size={16} />
                {submitting ? 'Bezig…' : 'Wachtwoord instellen'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
